import { useContext, useEffect, useState } from 'react'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { Dialog, DialogContent, DialogTitle, LinearProgress } from '@mui/material'
import { useApiGet } from '../../apiCalls/useApi'
import { getProviderDailyLogs } from '../../apiCalls/alertsApi'
import { OrgAppContext } from '../../OrgAppContext'

type ProviderDailyLogsProps = {
  open: boolean
  onClose: () => void
  appId: string
  providerId: string
  providerName: string
  date: string
}

type DailyLogType =
  | {
      Application: string
      Default_x0020_Program: string
      Program: string
      FullName: string
      ClientName: string
      MiscID: string
      Status: string
      TimeStamp: string
      TranID: string
      Type: string
    }
  | SmileLogType

type SmileLogType = {
  pid: number
  evt_additional_json: string | null
  evt_additional_json_vc: string
  auser_type: number
  have_headers: boolean
  remote_address: string
  request_id: string
  have_target_modules: boolean
  source_transaction_id: string
  have_target_resources: boolean
  have_target_users: boolean
  evt_timestamp: number
  transaction_guid: string
  type_display: string
  user_pid: number
  client_pid: number | null
  endpoint_module_pid: number
  type_pid: number
  target_res?: {
    pid: number
    req_val_result?: any | null
    res_id?: string | null
    res_version?: number | null
    res_version_str?: string | null
    event_pid?: number | null
    pers_module_pid?: number | null
  }
}

const summitColumns = [
  {
    field: 'Application',
    flex: 1,
  },
  {
    field: 'Default_x0020_Program',
    headerName: 'Default_x0020_Program',
    flex: 1.5,
  },
  {
    field: 'ProgramName',
    headerName: 'ProgramName',
    flex: 1.5,
  },
  {
    field: 'Type',
    headerName: 'Type',
    flex: 1.5,
  },
  {
    field: 'TimeStamp',
    headerName: 'TimeStamp',
    flex: 1.5,
  },
  {
    field: 'FullName',
    headerName: 'FullName',
    flex: 1,
  },
  {
    field: 'ClientName',
    headerName: 'ClientName',
    flex: 1,
  },
  {
    field: 'Status',
    headerName: 'Status',
    flex: 0.5,
  },
  {
    field: 'TranID',
    headerName: 'TranID',
    flex: 1,
  },
  {
    field: 'MiscID',
    headerName: 'MiscID',
    flex: 0.5,
  },
]

const smileColumns = [
  {
    field: 'pid',
    headerName: 'pid',
    flex: 0.5,
  },
  {
    field: 'remote_address',
    headerName: 'remote_address',
    flex: 0.8,
  },
  {
    field: 'evt_timestamp',
    headerName: 'evt_timestamp',
    flex: 0.8,
  },
  {
    field: 'user_pid',
    headerName: 'user_pid',
    flex: 0.5,
  },
  {
    field: 'type_display',
    headerName: 'type_display',
    flex: 1,
  },
  {
    field: 'transaction_guid',
    headerName: 'transaction_guid',
    flex: 1,
  },
  {
    field: 'target_res.res_id',
    headerName: 'target_res.res_id',
    flex: 1,
    valueGetter: (params: any) => params.row.target_res?.res_id || '',
  },
  {
    field: 'request_id',
    headerName: 'request_id',
    flex: 1,
  },
]

export const filterColumnsWithData = (columns: any[], logData: DailyLogType[]) => {
  if (!logData || logData.length === 0) return columns

  return columns.filter((column) => {
    const fieldName = column.field || column.dataKey

    // Check if any row has data for this field
    return logData.some((row) => {
      const value = row[fieldName as keyof DailyLogType]

      // Check if value exists and is not empty
      return value !== null &&
             value !== undefined &&
             value !== ''
    })
  })
}

export default function ProviderDailyLogs({
  open,
  onClose,
  appId,
  providerId,
  providerName,
  date,
}: ProviderDailyLogsProps) {
  const { orgId } = useContext(OrgAppContext)

  const [loading, setLoading] = useState(false)
  const [logs, setLogs] = useState<DailyLogType[]>([])

  const fetchProviderDailyLogs = useApiGet(getProviderDailyLogs)

  const baseColumns = orgId === 'smile-cdr' ? smileColumns : summitColumns
  const columns = filterColumnsWithData(baseColumns, logs)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    fetchProviderDailyLogs({
      appId,
      providerId,
      date,
    }).then((data) => {
      setLoading(false)
      if (data) {
        setLogs(data.data)
        console.log({ dailyLogs: data.data })
      }
    })
  }, [open, appId, providerId, date])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          width: '85vw',
          maxWidth: '85vw',
        },
      }}
    >
      <DialogTitle>
        {date} {providerName}
      </DialogTitle>
      <DialogContent>
        <DataGrid
          loading={loading}
          autoHeight
          hideFooterSelectedRowCount
          getRowId={(row) => ('TranID' in row ? row.TranID : row.pid)}
          getRowHeight={() => 'auto'}
          slots={{
            loadingOverlay: LinearProgress,
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
          columns={columns}
          rows={logs || []}
          sx={{
            border: 0,
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(235, 235, 235, 0.7)',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#FFFFFF',
              color: '#5B5B5B',
              fontSize: '15px',
              fontFamily: 'Segoe UI',
              borderBottom: '2px solid #BABABA',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid #BABABA`,
              fontSize: '12px',
              cursor: 'pointer',
              fontFamily: 'Segoe UI',
              fontWeight: '600',
              wordWrap: 'break-word',
              py: 1,
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            '& ::-webkit-scrollbar': {
              width: '5px',
            },
            '& ::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '& ::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '5px',
              '&:hover': {
                backgroundColor: '#555',
              },
            },
            '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': {
              color: '#5B5B5B',
            },
            '& .MuiDataGrid-toolbarContainer .MuiSvgIcon-root': {
              fill: '#5B5B5B',
            },
          }}
        />
      </DialogContent>
    </Dialog>
  )
}
